import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { api, ApiError } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { useAuthStore } from "@/stores/auth";

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        await api.auth.verifyEmail(token);
        setSuccess(true);
        // Update auth store so banner disappears immediately
        if (user) {
          setUser({ ...user, emailVerified: true });
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError("An unexpected error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token]);

  const handleResend = async () => {
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    setResending(true);
    try {
      await api.auth.resendVerification();
      setResent(true);
    } catch (err) {
      // If already verified, update state
      if (err instanceof ApiError && err.message === "Email already verified") {
        setUser({ ...user, emailVerified: true });
        setSuccess(true);
        setError("");
      }
    } finally {
      setResending(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 bg-background">
        <SEO
          title="Verify Email"
          description="Verify your email address for your GetSent account."
          canonical="/verify-email"
        />
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold">Invalid verification link</h2>
            <p className="mt-2 text-muted-foreground">
              This email verification link is invalid or missing.
            </p>
          </div>
          <Link to="/login" className="btn btn-primary">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-8 bg-background">
      <SEO
        title="Verify Email"
        description="Verify your email address for your GetSent account."
        canonical="/verify-email"
      />
      <div className="w-full max-w-md text-center space-y-6">
        {loading ? (
          <>
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
                <svg className="animate-spin h-8 w-8 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Verifying your email...</h2>
              <p className="mt-2 text-muted-foreground">
                Please wait while we verify your email address.
              </p>
            </div>
          </>
        ) : success ? (
          <>
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <svg className="h-8 w-8 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Email verified!</h2>
              <p className="mt-2 text-muted-foreground">
                Your email address has been verified successfully. You can now enjoy all features of GetSent.
              </p>
            </div>
            <button
              onClick={() => navigate({ to: "/dashboard" })}
              className="btn btn-primary"
            >
              Go to dashboard
            </button>
          </>
        ) : (
          <>
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Verification failed</h2>
              <p className="mt-2 text-muted-foreground">
                {error || "This verification link is invalid or has expired."}
              </p>
            </div>
            <div className="space-y-3">
              {user ? (
                resent ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/50">
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                      A new verification email has been sent to <strong>{user.email}</strong>. Please check your inbox.
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    className="btn btn-primary w-full"
                  >
                    {resending ? "Sending..." : "Resend verification email"}
                  </button>
                )
              ) : (
                <Link to="/login" className="btn btn-primary w-full">
                  Log in to resend
                </Link>
              )}
              <Link
                to="/dashboard"
                className="block w-full text-center text-sm text-muted-foreground hover:text-foreground"
              >
                Go to dashboard
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
